import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "@/hooks/useForm";
import { useLoadDocumentKinds } from "@/hooks/queries/useLoadDocumentKinds";
import {
  createLoadDocumentKind,
  updateLoadDocumentKind,
  type LoadDocumentKind,
} from "@/lib/api/load-document-kinds";
import type { LoadDocumentPhase } from "@/lib/api/enums";
import { queryKeys } from "@/lib/query-keys";
import { getLocalizedName } from "@/lib/localization";
import { fromIntlLanguage } from "@/lib/i18n";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { CustomSelect } from "@/components/ui/custom-select";
import { Surface } from "@/components/ui/surface";
import { Badge } from "@/components/ui/badge";
import { Label, PageHeading, Text } from "@/components/ui/text";
import { HStack } from "@/components/ui/h-stack";
import { VStack } from "@/components/ui/v-stack";
import { FormLabel } from "@/components/ui/form-label";
import { Skeleton } from "@/components/ui/skeleton";
import { Icon } from "@/components/ui/icons";
import { useToast } from "@/components/ui/toast";

// Phases offered in the "Required by phase" select (in checklist order).
// The empty value ("") maps to `required_at: null` (optional document).
const REQUIRED_AT_PHASES: readonly LoadDocumentPhase[] = [
  "BEFORE_DEPARTURE",
  "ORIGIN_CUSTOMS",
  "DESTINATION_CUSTOMS",
];

interface KindFormFields {
  nameEn: string;
  nameRu: string;
  nameUz: string;
  /** "" = optional; otherwise a LoadDocumentPhase value. */
  requiredAt: string;
  sortOrder: number;
}

const kindSchema = z.object({
  nameEn: z.string().min(1, "Required"),
  nameRu: z.string(),
  nameUz: z.string(),
  requiredAt: z.string(),
  sortOrder: z.number().int(),
});

function AdminLoadDocumentKindsCatalogPage() {
  const { t } = useTranslation("admin.catalogs.loadDocumentKinds");
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { data: kinds, isLoading, isError } = useLoadDocumentKinds();

  const [editing, setEditing] = useState<LoadDocumentKind | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const openAdd = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (kind: LoadDocumentKind) => {
    setEditing(kind);
    setModalOpen(true);
  };

  return (
    <VStack gap="xl" data-testid="admin-load-document-kinds-page">
      <HStack justify="between">
        <PageHeading>{t("title")}</PageHeading>
        <Button type="button" onClick={openAdd} data-testid="ldk-add">
          <Icon.Plus />
          {t("add")}
        </Button>
      </HStack>

      {isLoading && (
        <VStack gap="md">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" rounded="lg" />
          ))}
        </VStack>
      )}

      {isError && (
        <Text size="sm" color="danger">
          {t("failedToLoad")}
        </Text>
      )}

      {kinds && kinds.length === 0 && <Text color="muted">{t("noItems")}</Text>}

      {kinds && kinds.length > 0 && (
        <VStack gap="md">
          {kinds.map((kind) => (
            <Surface
              key={kind.id}
              bg="container"
              border="default"
              rounded="lg"
              padding="md"
              data-testid={`ldk-row-${kind.id}`}
            >
              <HStack justify="between" gap="sm">
                <HStack gap="sm" className="min-w-0">
                  <Text size="sm" color="muted" className="tabular-nums">
                    #{kind.sort_order}
                  </Text>
                  <Text weight="medium" truncate>
                    {getLocalizedName(
                      { id: kind.id, name: kind.names.uz, names: kind.names },
                      language,
                    )}
                  </Text>
                  {kind.required_at != null && (
                    <Badge color="warning" size="xs">
                      {t(`fields.phases.${kind.required_at}`)}
                    </Badge>
                  )}
                </HStack>
                <Button
                  type="button"
                  color="neutral"
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(kind)}
                  data-testid={`ldk-edit-${kind.id}`}
                >
                  {t("edit")}
                </Button>
              </HStack>
            </Surface>
          ))}
        </VStack>
      )}

      {modalOpen && (
        <LoadDocumentKindFormModal
          editing={editing}
          onClose={() => setModalOpen(false)}
        />
      )}
    </VStack>
  );
}

function LoadDocumentKindFormModal({
  editing,
  onClose,
}: {
  editing: LoadDocumentKind | null;
  onClose: () => void;
}) {
  const { t } = useTranslation("admin.catalogs.loadDocumentKinds");
  const { t: tCommon } = useTranslation("common");
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const mutation = useMutation({
    mutationFn: (fields: KindFormFields) => {
      const payload = {
        names: toLocalizedName({
          en: fields.nameEn,
          ru: fields.nameRu,
          uz: fields.nameUz,
        }),
        required_at:
          fields.requiredAt === ""
            ? null
            : (fields.requiredAt as LoadDocumentPhase),
        sort_order: fields.sortOrder,
      };
      return editing
        ? updateLoadDocumentKind(editing.id, payload)
        : createLoadDocumentKind(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.loadDocumentKinds(),
      });
      onClose();
    },
    onError: () => addToast(t("failedToSave"), "error"),
  });

  const form = useForm({
    schema: kindSchema,
    defaults: {
      nameEn: editing?.names.en ?? "",
      nameRu: editing?.names.ru ?? "",
      nameUz: editing?.names.uz ?? "",
      requiredAt: editing?.required_at ?? "",
      sortOrder: editing?.sort_order ?? 0,
    },
    onSubmit: async (data) => {
      await mutation.mutateAsync(data);
    },
  });

  const namesDraft: LocalizedNamesDraft = {
    en: form.fields.nameEn,
    ru: form.fields.nameRu,
    uz: form.fields.nameUz,
  };

  return (
    <AdaptiveSheet
      open
      onDismiss={() => {
        if (!form.isSubmitting) onClose();
      }}
      onSubmit={() => void form.submitSafe()}
      size="md"
    >
      <ModalHeader>
        <Text>{editing ? t("modalEditTitle") : t("modalAddTitle")}</Text>
        <IconButton
          type="button"
          onClick={onClose}
          aria-label={tCommon("close")}
          title={tCommon("close")}
        >
          <Icon.X size="md" />
        </IconButton>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <LocalizedNamesFields
          value={namesDraft}
          onChange={(next) =>
            form.setFields({
              nameEn: next.en,
              nameRu: next.ru,
              nameUz: next.uz,
            })
          }
          disabled={form.isSubmitting}
          testIdPrefix="ldk"
        />

        <VStack gap="xs">
          <CustomSelect
            label={t("fields.requiredAtLabel")}
            labelPosition="above"
            value={form.fields.requiredAt}
            onChange={(value) => form.setField("requiredAt", value)}
            disabled={form.isSubmitting}
            fullWidth
            options={[
              { value: "", label: t("fields.phases.optional") },
              ...REQUIRED_AT_PHASES.map((phase) => ({
                value: phase,
                label: t(`fields.phases.${phase}`),
              })),
            ]}
            data-testid="ldk-required-at"
          />
          <Label size="xs" color="muted">
            {t("fields.requiredAtHelper")}
          </Label>
        </VStack>

        <VStack gap="xs">
          <FormLabel>{t("fields.sortOrderLabel")}</FormLabel>
          <Input
            type="number"
            value={String(form.fields.sortOrder)}
            onChange={(e) =>
              form.setField("sortOrder", Number(e.target.value) || 0)
            }
            disabled={form.isSubmitting}
            data-testid="ldk-sort-order"
          />
          <Label size="xs" color="muted">
            {t("fields.sortOrderHelper")}
          </Label>
        </VStack>
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          color="neutral"
          variant="outline"
          onClick={onClose}
          disabled={form.isSubmitting}
        >
          {tCommon("cancel")}
        </Button>
        <Button
          type="button"
          onClick={() => void form.submitSafe()}
          disabled={form.isSubmitting || !form.isValid}
          data-testid="ldk-save"
        >
          {form.isSubmitting ? <Icon.Spinner /> : null}
          {tCommon("save")}
        </Button>
      </ModalFooter>
    </AdaptiveSheet>
  );
}

export const Route = createFileRoute(
  "/_app/admin/catalogs/load-document-kinds",
)({
  component: AdminLoadDocumentKindsCatalogPage,
});
